import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckIcon, PlusCircleIcon } from "lucide-react";
import { Badge } from "src/components/ui/badge";
import { Button } from "src/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "src/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import { Separator } from "src/components/ui/separator";
import { cn } from "src/lib";

interface WorkflowBoardFilterOptionsProps {
  title?: string;
  options: {
    label: string;
    value: string;
    icon?: React.ComponentType<{ className?: string }>;
  }[];
  selected?: string[];
  counter?: { [key: string]: number };
  minSelection?: number;
  onUpdateSelected?: (selected: string[]) => void;
}

export function WorkflowBoardFilterOptions({
  title,
  options,
  selected,
  counter,
  minSelection = 0,
  onUpdateSelected,
}: WorkflowBoardFilterOptionsProps) {
  const [t] = useTranslation();
  const [selectedValues, setSelectedValues] = useState(new Set(selected || []));

  const disabled = useMemo(
    () => selectedValues.size <= minSelection,
    [minSelection, selectedValues]
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 border-dashed">
          <PlusCircleIcon className="mr-2 h-4 w-4" />
          {title}
          {selectedValues?.size > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge
                variant="secondary"
                className="rounded-sm px-1 font-normal lg:hidden"
              >
                {selectedValues.size}
              </Badge>
              <div className="hidden space-x-1 lg:flex">
                {selectedValues.size > 2 ? (
                  <Badge
                    variant="secondary"
                    className="rounded-sm px-1 font-normal"
                  >
                    {t(
                      "workflows:board.filter-options.selected",
                      "{{count}} selected",
                      { count: selectedValues.size }
                    )}
                  </Badge>
                ) : (
                  options
                    .filter((option) => selectedValues.has(option.value))
                    .map((option) => (
                      <Badge
                        variant="secondary"
                        key={option.value}
                        className="rounded-sm px-1 font-normal"
                      >
                        {option.label}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandInput
            className="border-none focus:outline-none focus:ring-0"
            placeholder={title}
          />
          <CommandList>
            <CommandEmpty>
              {t("workflows:board.filter-options.empty", "No results found.")}
            </CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedValues.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    disabled={Boolean(disabled && isSelected)}
                    onSelect={() => {
                      if (isSelected) {
                        selectedValues.delete(option.value);
                      } else {
                        selectedValues.add(option.value);
                      }
                      const filterValues = Array.from(selectedValues);
                      setSelectedValues(new Set(filterValues));

                      if (onUpdateSelected) {
                        onUpdateSelected(filterValues);
                      }
                    }}
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-indigo-400",
                        isSelected
                          ? "text-white bg-indigo-400"
                          : "opacity-50 [&_svg]:invisible"
                      )}
                    >
                      <CheckIcon className={cn("h-4 w-4")} />
                    </div>
                    {option.icon && (
                      <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                    )}
                    <span>{option.label}</span>
                    {counter && typeof counter[option.value] === "number" && (
                      <span className="ml-auto flex h-4 w-4 items-center justify-center font-mono text-xs">
                        {counter[option.value]}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {!minSelection && selectedValues.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      setSelectedValues(new Set());
                      if (onUpdateSelected) {
                        onUpdateSelected([]);
                      }
                    }}
                    className="justify-center text-center"
                  >
                    {t("workflows:board.filter-options.clear", "Clear filters")}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
